import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { clientApi, ApiError } from '../lib/api';
import type { ClientBundle } from '../../shared/types';
import ClientReview from './ClientReview';
import ClientInterviewView from './ClientInterviewView';
import BundleSwitcher from '../components/BundleSwitcher';
import BundleErrorState from '../components/BundleErrorState';
import { bundleErrorCopy, type BundleErrorKind } from '../lib/bundleErrorCopy';
import {
  BundleReviewContext,
  type BundleReviewContextValue,
  type BundleReviewReadyItem,
} from '../lib/bundleReviewContext';
import { BundleInterviewContext } from '../lib/bundleInterviewContext';
import Eyebrow from '../components/Eyebrow';
import Button from '../components/Button';

/**
 * Thin wrapper mounted at /b/:bundleSlug/:slug. Adds a sticky switcher bar
 * above an otherwise-untouched <ClientReview/> (review-kind bundles) or the
 * read-only <ClientInterviewView/> (shoot-kind bundles — docs/script-bundles-plan.md
 * §5 "Shoot pack"), and, for review, the post-approval "Next script"
 * follow-on via BundleReviewContext.
 *
 * The bundle fetch here doubles as the kind gate — ClientReview/ClientInterviewView
 * load the actual script independently via their own
 * /api/client/scripts/:slug call (reading :slug off the same matched route),
 * but which of the two even MOUNTS depends on bundle.kind, so nothing renders
 * a script view until this fetch settles: while it's in-flight, a minimal
 * neutral loading state shows (no switcher, no script view). When the bundle
 * *itself* is rotated/archived/unknown (404/410/423) that's real link
 * semantics, so it renders the same dedicated error page the hub would show.
 *
 * A network-class failure (drop, 5xx) gets exactly one short-delay retry —
 * there's deliberately no "just render ClientReview and hope" fallback here
 * the way BundleHub's card list can afford to degrade: kind is unknowable at
 * that point, and guessing review-kind on what might actually be a shoot pack
 * would hand a crew member a live approve/comment/suggest surface. If the
 * retry also fails, a neutral (theme-unforced) error state offers a manual
 * Try again, which simply re-runs the same fetch.
 *
 * This fetch/error/membership logic is shared by both kinds; only the final
 * render branches on bundle.kind.
 */
export default function BundleScriptView() {
  const { bundleSlug, slug } = useParams<{ bundleSlug: string; slug: string }>();
  const [bundle, setBundle] = useState<ClientBundle | null>(null);
  const [bundleError, setBundleError] = useState<BundleErrorKind | null>(null);
  // True only once a network-class failure has also failed its one retry —
  // distinguishes "still loading/retrying" from "give up, ask the visitor to
  // retry manually" so the loading gate below knows when to stop waiting even
  // though `bundle` never arrives.
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by the "Try again" button to re-run the fetch effect without
  // changing bundleSlug/slug.
  const [retryNonce, setRetryNonce] = useState(0);
  // Tracks which bundleSlug we last fetched for. Navigating /b/A/x → /b/B/y
  // changes bundleSlug — without this, bundle/bundleError/loadFailed from
  // bundle A would linger and get evaluated (membership, kind) against
  // bundle B until the new fetch lands. Same-bundle slug-only navigation
  // (switcher clicks, "Next script") must NOT reset — that's what keeps the
  // switcher/follow-on on screen without flicker while the next script's
  // membership re-resolves.
  const lastBundleSlugRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!bundleSlug) return;

    if (lastBundleSlugRef.current !== bundleSlug) {
      lastBundleSlugRef.current = bundleSlug;
      setBundle(null);
      setBundleError(null);
      setLoadFailed(false);
    }

    let cancelled = false;
    let retried = false;

    function attempt() {
      clientApi
        .getBundle(bundleSlug!)
        .then((b) => {
          if (cancelled) return;
          setBundle(b);
          setBundleError(null);
          setLoadFailed(false);
        })
        .catch((err) => {
          if (cancelled) return;
          if (err instanceof ApiError && err.status === 410) {
            setBundleError('replaced');
            return;
          }
          if (err instanceof ApiError && err.status === 404) {
            setBundleError('not_found');
            return;
          }
          if (err instanceof ApiError && err.status === 423) {
            setBundleError('unavailable');
            return;
          }
          // Network drop / 5xx: one short-delay retry before giving up —
          // see the file-level doc for why there's no ClientReview fallback.
          if (!retried) {
            retried = true;
            window.setTimeout(() => {
              if (!cancelled) attempt();
            }, 1200);
            return;
          }
          setLoadFailed(true);
        });
    }

    attempt();

    return () => {
      cancelled = true;
    };
    // Refetch on :slug too (not just :bundleSlug) — following "Next script"
    // inside the bundle should immediately reflect the just-approved script
    // in the switcher's ✓ state and in the follow-on's candidate list. The
    // existing `bundle` stays on screen while this resolves, so there's no
    // flicker or loss of the switcher between navigations. retryNonce exists
    // purely so the "Try again" button can re-trigger this effect.
  }, [bundleSlug, slug, retryNonce]);

  const readyItems = useMemo<BundleReviewReadyItem[]>(
    () => (bundle ? bundle.items.filter((i): i is BundleReviewReadyItem => i.kind === 'script') : []),
    [bundle],
  );

  if (bundleError) {
    return <BundleErrorState {...bundleErrorCopy(bundleError)} />;
  }

  // Initial load (or a bundleSlug change — see lastBundleSlugRef above).
  // Once `bundle` is set it stays set across later same-bundle slug
  // re-fetches, so this gate never re-triggers on in-bundle navigation.
  if (!bundle && !loadFailed) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center">
        <p className="text-faint font-mono text-[12px] uppercase tracking-[0.12em]">Loading…</p>
      </main>
    );
  }

  // Network-class failure that didn't recover on retry. Theme-unforced (kind
  // is unknown) — matches whatever theme is already active rather than
  // guessing light or dark.
  if (loadFailed) {
    return (
      <main className="min-h-screen bg-page text-fg flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Eyebrow accent>// SOMETHING WENT WRONG</Eyebrow>
          <h1 className="font-display font-bold text-[32px] leading-[1.1] tracking-[-0.02em] mt-3 mb-3">
            We couldn’t load this page.
          </h1>
          <p className="text-muted leading-[1.55]">
            It’s most likely temporary: your connection, or a brief hiccup on our end.
          </p>
          <div className="mt-6">
            <Button
              variant="primary"
              glow
              onClick={() => {
                setLoadFailed(false);
                setRetryNonce((n) => n + 1);
              }}
            >
              Try again
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Membership is a UX concern, not a security one — /s/:slug is already
  // public, so an unrecognised or not-yet-ready slug just bounces to the hub
  // rather than erroring.
  if (bundle && slug && !readyItems.some((i) => i.slug === slug)) {
    return <Navigate to={`/b/${bundleSlug}`} replace />;
  }

  // Shoot pack: read-only interview view, dark switcher (no ✓ ticks — no
  // approval concept here), no BundleReviewContext (there's no "Next
  // script"/approve follow-on to offer).
  if (bundle?.kind === 'shoot') {
    return (
      <BundleInterviewContext.Provider value={bundleSlug ? { bundleSlug } : null}>
        {bundleSlug && slug && (
          <BundleSwitcher
            bundleSlug={bundleSlug}
            items={readyItems}
            currentSlug={slug}
            showTicks={false}
          />
        )}
        {/* key={slug}: see the matching comment on <ClientReview/> below —
            same reasoning, same fix. */}
        <ClientInterviewView key={slug} />
      </BundleInterviewContext.Provider>
    );
  }

  const contextValue: BundleReviewContextValue | null =
    bundle && bundleSlug ? { bundleSlug, bundleTitle: bundle.title, items: readyItems } : null;

  return (
    <BundleReviewContext.Provider value={contextValue}>
      {/* No sticky BundleSwitcher here (client-review redesign, 2026-07-18):
          ClientReview renders its own script picker inside its
          utility bar via BundleReviewContext, so a second nav bar above it
          would be a duplicate. Shoot-kind bundles above still use
          BundleSwitcher — that view is untouched. */}
      {/* key={slug}: without it, React reuses the mounted ClientReview
          instance across a switcher navigation (only the :slug param
          changes, same route element) — its content/comments/suggestion
          state would linger at the previous script's URL until the new
          script's fetches land, and rapid switching could interleave stale
          responses. Forcing a fresh instance per slug is the simplest
          correct fix; ClientReview itself is untouched otherwise. */}
      <ClientReview key={slug} />
    </BundleReviewContext.Provider>
  );
}
